import { useEffect } from "react";
import { usePaneStore } from "../state/store-context";
import type { OperatorPreferences } from "@/hooks/usePreferences";

const STORAGE_KEY = "capture_operator_preferences";

/**
 * On mount, load preferences from localStorage and apply them to the verification store.
 * This runs once when the verification station mounts — ensuring operator preferences
 * are applied before the first document loads.
 */
export function useApplyPreferences(): void {
  const setSnippetZoom = usePaneStore((s) => s.setSnippetZoom);
  const toggleSnippetPanel = usePaneStore((s) => s.toggleSnippetPanel);
  const snippetVisible = usePaneStore((s) => s.snippetVisible);
  const setBboxOverlaysVisible = usePaneStore((s) => s.setBboxOverlaysVisible);
  const setSessionTimerVisible = usePaneStore((s) => s.setSessionTimerVisible);
  const setBboxLegendVisible = usePaneStore((s) => s.setBboxLegendVisible);
  const setSavedViewerZoom = usePaneStore((s) => s.setSavedViewerZoom);
  const setFitMode = usePaneStore((s) => s.setFitMode);
  const setVsAutoCenterOnFocus = usePaneStore((s) => s.setVsAutoCenterOnFocus);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const prefs = JSON.parse(raw) as Partial<OperatorPreferences>;

      if (typeof prefs.snippetZoomDefault === "number" && prefs.snippetZoomDefault >= 0) {
        setSnippetZoom(prefs.snippetZoomDefault);
      }

      if (typeof prefs.showSnippetPanel === "boolean") {
        // Only toggle if the current state differs from the preference
        if (prefs.showSnippetPanel !== snippetVisible) {
          toggleSnippetPanel();
        }
      }

      if (typeof prefs.showBboxOverlays === "boolean") {
        setBboxOverlaysVisible(prefs.showBboxOverlays);
      }

      // E-004 — session timer visibility.
      if (typeof prefs.showSessionTimer === "boolean") {
        setSessionTimerVisible(prefs.showSessionTimer);
      }

      if (typeof prefs.showBboxLegend === "boolean") {
        setBboxLegendVisible(prefs.showBboxLegend);
      }

      // R-149 — apply persisted viewer zoom so doc N+1 inherits doc N's zoom.
      if (typeof prefs.viewerZoom === "number" && prefs.viewerZoom > 0) {
        setSavedViewerZoom(prefs.viewerZoom);
      } else if (prefs.viewerZoom === null) {
        setSavedViewerZoom(null);
      }

      // R-198 — restore the persisted fit mode so a chosen Fit Width / Fit
      // Page survives doc/batch transitions instead of resetting on every nav.
      if (prefs.viewerFitMode === "page" || prefs.viewerFitMode === "width") {
        setFitMode(prefs.viewerFitMode);
      }

      // R-188 — restore the auto-center-on-focus preference.
      if (typeof prefs.vsAutoCenterOnFocus === "boolean") {
        setVsAutoCenterOnFocus(prefs.vsAutoCenterOnFocus);
      }
    } catch {
      // Corrupt localStorage — ignore, defaults are fine
    }
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
